import {defineMessages, useIntl} from 'react-intl';
import {RefObject, useEffect, useRef, useState} from 'react';

import {arrowUp, paperclip} from '@sail/icons/react/Icon';
import {Button, css, Icon, tokens, view} from '@sail/ui';

import type {FileError, StagedFile} from '@stripe-internal/conversations-ui';
import {
  StagedFileList,
  UploadErrorOverlay,
  useTheme,
} from '@stripe-internal/conversations-ui';

export type MobileInputProps = {
  disabled?: boolean;
  sendMessageDisabled?: boolean;
  attachmentDisabled?: boolean;
  hidden?: boolean;
  placeholder?: string;
  uploadErrors?: FileError[];
  stagedFiles?: StagedFile[];
  onRemoveStagedFile?: (index: number) => void;
  onSendFile?: (file: File) => Promise<void>;
  onSendMessage: (message: string) => Promise<void>;
  onSendTyping: () => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChanged?: (value: string) => void;
};

const messages = defineMessages({
  sendMessageAriaLabel: {
    id: 'mobile_conversations_assistant.mobileInput.sendButton.ariaLabel',
    description: 'Label for the chat textbox send button',
    defaultMessage: 'Send message',
  },
  selectAttachmentAriaLabel: {
    id: 'mobile_conversations_assistant.mobileInput.selectAttachment.ariaLabel',
    description: 'Label for the chat textbox select attachment',
    defaultMessage: 'Select attachment',
  },
});

const HiddenFileInput = ({
  onChange,
  fileInputRef,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => (
  <view.input
    data-testid="file-input"
    type="file"
    value=""
    onChange={onChange}
    css={{display: 'none'}}
    ref={fileInputRef}
  />
);

function useIsOverflowing(
  textareaRef: RefObject<HTMLTextAreaElement>,
  dependencies: unknown[],
): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textareaRef.current) {
        setIsOverflowing(
          textareaRef.current.scrollHeight > textareaRef.current.clientHeight,
        );
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return isOverflowing;
}

function useAutoResize(
  textareaRef: RefObject<HTMLTextAreaElement>,
  text: string,
): void {
  const cloneRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!textareaRef.current) return;

    if (!cloneRef.current) {
      const clone = document.createElement('textarea');
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '-9999px';
      clone.style.visibility = 'hidden';
      clone.style.pointerEvents = 'none';
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      document.body.appendChild(clone);
      cloneRef.current = clone;
    }

    const original = textareaRef.current;
    const clone = cloneRef.current;

    const computedStyle = window.getComputedStyle(original);
    clone.style.width = computedStyle.width;
    clone.style.padding = computedStyle.padding;
    clone.style.border = computedStyle.border;
    clone.style.fontSize = computedStyle.fontSize;
    clone.style.fontFamily = computedStyle.fontFamily;
    clone.style.lineHeight = computedStyle.lineHeight;
    clone.style.boxSizing = computedStyle.boxSizing;
    clone.style.letterSpacing = computedStyle.letterSpacing;
    clone.style.wordWrap = computedStyle.wordWrap;
    clone.style.whiteSpace = computedStyle.whiteSpace;

    clone.value = text;

    clone.style.height = '20px';
    const newHeight = clone.scrollHeight;

    original.style.height = `${newHeight}px`;

    return () => {
      if (cloneRef.current && document.body.contains(cloneRef.current)) {
        document.body.removeChild(cloneRef.current);
        cloneRef.current = null;
      }
    };
  }, [text, textareaRef]);
}

const AttachmentButton = ({
  onSelect,
  handleFileInputKeyDown,
  fileInputRef,
  onPointerDown,
}: {
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileInputKeyDown: (e: React.KeyboardEvent) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onPointerDown: (e: React.PointerEvent) => void;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <view.label
      aria-label={intl.formatMessage(messages.selectAttachmentAriaLabel)}
      onPointerDown={onPointerDown}
      css={{
        border: 'none',
        cursor: 'pointer',
        position: 'absolute',
        right: '40px',
        bottom: '12px',
        marginTop: 0,
        marginRight: 'small',
        zIndex: 2,
      }}
      tabIndex={0}
      onKeyDown={handleFileInputKeyDown}
    >
      <Icon
        icon={paperclip}
        data-testid="attachment-icon"
        css={{color: 'action.primary.disabled', ':hover': {color: 'subdued'}}}
      />
      <HiddenFileInput onChange={onSelect} fileInputRef={fileInputRef} />
    </view.label>
  );
};

export function MobileInput({
  disabled,
  hidden,
  placeholder,
  attachmentDisabled,
  stagedFiles,
  onRemoveStagedFile,
  uploadErrors,
  sendMessageDisabled,
  onSendMessage,
  onSendTyping,
  onSendFile,
  onInputFocus,
  onInputBlur,
  onInputChanged,
}: MobileInputProps) {
  const theme = useTheme();
  const [enteredText, setEnteredText] = useState('');
  const isDisabled = disabled || hidden;
  const hasStagedFiles = (stagedFiles?.length ?? 0) > 0;
  const isUploading = stagedFiles?.some((f) => f.status === 'uploading');
  const isSendDisabled =
    isDisabled ||
    sendMessageDisabled ||
    isUploading ||
    (enteredText === '' && !hasStagedFiles);
  const showAttachmentButton =
    !!onSendFile && !attachmentDisabled && !isDisabled;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsOverflowing(textareaRef, [enteredText]);

  useAutoResize(textareaRef, enteredText);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      window.dispatchEvent(new Event('inputresize'));
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const sendMessage = () => {
    if (!sendMessageDisabled && !isUploading) {
      const messageBody = enteredText.trim();
      setEnteredText('');

      if (messageBody || hasStagedFiles) {
        onSendMessage(messageBody);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Note: We are intentionally using a deprecated property, keyCode, in support for IME(Input Method Editor) Enters.
    // Why: For some languages like Japanese, IME is used to transform qwerty inputs into characters, and the user needs to hit Enter to select.
    // However, we are also looking to send the user message on Enter. So we need a way to differentiate a normal Enter vs IME Enter.
    // In the deprecated properties like 'keyCode' and 'which', the two Enters are represented with codes '13' (normal) and '229' (IME).
    // In nondeprecated properties like 'key' and 'code', both are represented as 'Enter'.
    // Since keyCode is still supported in majority of the browsers today, and the legacy Copilot input used keyCode for the longest time, we'll continue to use keyCode here for now.
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      sendMessage();
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const newEnteredText = event.target.value;
    setEnteredText(newEnteredText);
    onInputChanged?.(newEnteredText);
    if (newEnteredText.length) {
      onSendTyping?.();
    }
  };

  const handleFileSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    e.target.files && onSendFile && onSendFile(e.target.files[0]);
  };

  const handleFileInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleAttachmentPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
  };

  return (
    <view.div
      ref={containerRef}
      css={{
        paddingX: 'medium',
        paddingBottom: 'medium',
        flexGrow: 0,
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      <view.div
        data-testid="text-input-container"
        onClick={() => textareaRef.current?.focus()}
        css={{
          position: 'relative',
          keyline: tokens.color.border.toString(),
          borderRadius: '24px',
          overflow: 'visible',
          boxShadow: isDisabled ? 'none' : 'overlay.inverted',
          backgroundColor: isDisabled ? tokens.color.neutral[50] : 'surface',
          width: 'fill',
          stack: 'y',
          transition: '.5s background-color, .5s box-shadow',
          cursor: isDisabled ? 'default' : 'text',
          ':focus-within': {
            keyline: theme.primaryActionColor,
            keylineWidth: 'small',
          },
        }}
      >
        <UploadErrorOverlay uploadErrors={uploadErrors} />
        {stagedFiles && onRemoveStagedFile && stagedFiles.length > 0 ? (
          <StagedFileList
            stagedFiles={stagedFiles}
            onRemoveStagedFile={onRemoveStagedFile}
          />
        ) : null}
        <view.div
          css={{
            position: 'relative',
            stack: 'x',
            alignY: 'center',
            width: 'fill',
          }}
        >
          <view.textarea
            ref={textareaRef}
            key="textarea"
            data-testid="text-input"
            autoFocus
            disabled={isDisabled}
            placeholder={placeholder}
            value={enteredText || ''}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            css={{
              appearance: 'none',
              border: 'none',
              lineHeight: 'label.large',
              fontSize: '16px',
              paddingY: '12px',
              paddingLeft: '20px',
              backgroundColor: 'transparent',
              caretColor: theme.textCaretColor,
              '::placeholder': {
                color: 'form.disabled',
              },
              resize: 'none',
              ':focus': {outline: 'none'},
              overflowY: 'auto',
            }}
            uses={[
              css({
                maxHeight: '90vh',
                minHeight: '20px',
                paddingRight: isOverflowing ? '64px' : '48px',
              }),
            ]}
          />
          {showAttachmentButton ? (
            <AttachmentButton
              onSelect={handleFileSelection}
              handleFileInputKeyDown={handleFileInputKeyDown}
              fileInputRef={fileInputRef}
              onPointerDown={handleAttachmentPointerDown}
            />
          ) : null}
          <SendMessageButton
            isSendDisabled={isSendDisabled}
            sendMessage={sendMessage}
            setEnteredText={setEnteredText}
          />
        </view.div>
      </view.div>
    </view.div>
  );
}

function SendMessageButton({
  isSendDisabled,
  sendMessage,
  setEnteredText,
}: {
  isSendDisabled: boolean;
  sendMessage: () => void;
  setEnteredText: (text: string) => void;
}) {
  const theme = useTheme();
  const handlePointerDown = (event: React.PointerEvent) => {
    if (isSendDisabled) return;
    event.preventDefault();
    sendMessage();
  };

  const VISUAL_DIAMETER = 40;
  const EXTRA_PADDING = 8;
  const HIT_SIZE = VISUAL_DIAMETER + EXTRA_PADDING * 2;

  const intl = useIntl();

  return (
    <Button
      aria-label={intl.formatMessage(messages.sendMessageAriaLabel)}
      onPress={() => {
        if (isSendDisabled) return;
        sendMessage();
        setEnteredText('');
      }}
      onPointerDown={handlePointerDown}
      key="button"
      size="medium"
      disabled={isSendDisabled}
      data-testid="send-button"
      css={{
        borderRadius: 'rounded',
        aspectRatio: '1 / 1',
        position: 'absolute',
        right: '8px',
        bottom: '10px',
        zIndex: 2,
        marginRight: 0,
        '::before': {
          content: "''",
          position: 'absolute',
          left: `calc(50% - ${HIT_SIZE / 2}px)`,
          top: `calc(50% - ${HIT_SIZE / 2}px)`,
          width: `${HIT_SIZE}px`,
          height: `${HIT_SIZE}px`,
          backgroundColor: 'transparent',
          pointerEvents: isSendDisabled ? 'none' : 'auto',
          borderRadius: '50%',
          zIndex: 3,
        },

        '::after': {
          borderRadius: 'rounded',
        },
      }}
      uses={[
        css({
          position: 'absolute',
          right: '8px',
          bottom: '8px',
          zIndex: 2,
          marginRight: 0,
        }),
        isSendDisabled
          ? css({
              backgroundColor: tokens.color.neutral[50],
              boxShadow: 'none',
              color: 'form.disabled',
              '::after': {
                display: 'none',
              },
              '::before': {
                pointerEvents: 'none',
              },
            })
          : css({
              backgroundColor: theme.primaryActionColor,
              color: 'white',
              borderColor: theme.primaryActionColor,
              ':hover:not(:active):not([aria-disabled="true"])': {
                backgroundColor: theme.primaryActionColor,
                keyline: theme.ctaHoverColor,
                topShadow: theme.primaryActionColor,
                color: 'white',
              },
              ':active:not([aria-disabled="true"])': {
                topShadow: theme.pressedShadow,
                backgroundColor: theme.primaryActionColor,
                keyline: theme.primaryActionColor,
                color: 'white',
              },
            }),
      ]}
    >
      <Icon icon={arrowUp} size="xsmall" />
    </Button>
  );
}
